import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CustomFieldType } from './CustomFieldTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { optionPresets } from './customFieldOptionPresets';

export interface ConditionalFieldsEditorProps {
    field: CustomFieldType;
    childFields: CustomFieldType[];
    allowExtendedOptions?: boolean;
    setFields: React.Dispatch<React.SetStateAction<CustomFieldType[]>>;
}

export type ConditionOption = {
    value: string | number | boolean;
    name: string;
};

// Determine wheter two values are the same condition value
export const isSameConditionValue = (
    firstValue: string | number | boolean | null | undefined,
    secondValue: string | number | boolean | null | undefined
) => {
    return String(firstValue) === String(secondValue);
};

// Get the answer options a conditional question can be linked to
export const getConditionOptions = (
    field: CustomFieldType,
    t: (key: string) => string
): ConditionOption[] => {

    // Set hardcoded options for the boolean field type
    if (field.field_type === 'boolean') {
        return [
            { value: true, name: t('forms.true_value') },
            { value: false, name: t('forms.false_value') },
        ];
    }

    // Get the field types which need manual created or preset options
    const usesCustomOptions = ['tabs', 'checkbox', 'dropdown'].includes(field.field_type);
    const usesPresetOptions = ['yes_no', 'acceptable', 'compliant'].includes(field.field_type);

    // Return if there are no custom or preset options found
    if (!usesCustomOptions && !usesPresetOptions) {
        return [];
    }

    // Fall back to the presets when a preset field has no stored options
    const hasFieldOptions = (field.options || []).some(option => !option.deleted);

    const options = usesCustomOptions
        ? field.options || []
        : hasFieldOptions
            ? field.options || []
            : optionPresets[field.field_type] || [];

    // Filter the options
    return options
        .filter(option => !option.deleted)
        .map(option => ({
            value: option.id || option.uuid || option.value || '',
            name: t(option.label),
        }))
        .filter(option => option.value !== '');
};

// Get the active child field linked to a condition option
export const getChildFieldForConditionOption = (
    field: CustomFieldType,
    childFields: CustomFieldType[],
    conditionOption: ConditionOption
): CustomFieldType | null => {

    // Match on the boolean condition for boolean parents
    if (field.field_type === 'boolean') {
        return childFields.find(child_field =>
            !child_field.deleted &&
            isSameConditionValue(child_field.show_when_boolean, conditionOption.value)
        ) ?? null;
    }

    // Match on the option condition for option based parents
    return childFields.find(child_field =>
        !child_field.deleted &&
        isSameConditionValue(child_field.show_when_option_id, conditionOption.value)
    ) ?? null;
};

const ConditionalFieldsEditor: React.FC<ConditionalFieldsEditorProps> = ({
    field,
    childFields,
    allowExtendedOptions,
    setFields,
}) => {
    const { t } = useTranslation();
    const parentIdentifier = field.id || field.uuid;

    // Get the answer options of the parent field
    const conditionOptions = useMemo(() => {
        return getConditionOptions(field, t);
    }, [field, t]);

    // Add a conditional question for the given answer option
    const handleAddConditionalQuestion = (conditionOption: ConditionOption) => {
        if (!allowExtendedOptions) {
            return;
        }

        setFields(current => {

            // Get the current version of the parent field
            const parentField = current.find(custom_field =>
                (custom_field.id || custom_field.uuid) === parentIdentifier
            );

            // Return unchanged when the parent field is not found
            if (!parentField) {
                return current;
            }

            // Initialize the condition value and the fields to update
            let conditionValue = conditionOption.value;
            let updatedFields = current;

            // Materialize the preset options on the parent when they only exist as presets
            const presetOptions = optionPresets[parentField.field_type];
            const hasFieldOptions = (parentField.options || []).some(option => !option.deleted);

            if (parentField.field_type !== 'boolean' && presetOptions && !hasFieldOptions) {

                // Copy the presets into real options with an uuid
                const materializedOptions = presetOptions.map((option, index) => ({
                    ...option,
                    uuid: uuidv4(),
                    ordering: index + 1,
                }));

                // Bind the condition to the materialized option
                const materializedOption = materializedOptions.find(option =>
                    isSameConditionValue(option.value, conditionOption.value)
                );

                if (materializedOption) {
                    conditionValue = materializedOption.uuid as string;
                }

                // Store the materialized options on the parent field
                updatedFields = current.map(custom_field =>
                    (custom_field.id || custom_field.uuid) === parentIdentifier
                        ? { ...custom_field, options: materializedOptions }
                        : custom_field
                );
            }

            // Count the active conditional questions of the parent
            const activeChildCount = updatedFields.filter(custom_field =>
                custom_field.parent_field_id === parentIdentifier &&
                !custom_field.deleted
            ).length;

            // Create the new conditional question
            const newField: CustomFieldType = {
                uuid: uuidv4(),
                linked_object_type: parentField.linked_object_type,
                linked_object_value: parentField.linked_object_value,
                group_id: parentField.group_id || null,
                parent_field_id: parentIdentifier,
                label: '',
                description: '',
                placeholder: '',
                field_type: 'input',
                required: false,
                hide_externally: false,
                ordering: activeChildCount + 1,
                options: [],
                show_when_option_id: parentField.field_type === 'boolean' ? undefined : conditionValue as string | number,
                show_when_boolean: parentField.field_type === 'boolean' ? conditionValue as boolean : undefined,
            };

            return [...updatedFields, newField];
        });
    };

    // Remove the conditional question of the given answer option
    const handleRemoveConditionalQuestion = (childField: CustomFieldType) => {
        if (!allowExtendedOptions) {
            return;
        }

        const childIdentifier = childField.id || childField.uuid;

        setFields(current => {
            return current.map(custom_field =>
                (custom_field.id || custom_field.uuid) === childIdentifier
                    ? { ...custom_field, deleted: true }
                    : custom_field
            );
        });
    };

    // Don't render if there are no condition options
    if (conditionOptions.length === 0) {
        return null;
    }

    return (
        <div className='conditional-fields'>
            <div className='form-group'>
                <div className='tooltip-row'>
                    <label>
                        {t('custom_field.general.conditional_questions_label')}
                    </label>
                    <span className="tooltip-icon">
                        <FontAwesomeIcon icon={faCircleQuestion} />
                        <span className="tooltip tooltip-right">
                            {t('custom_field.general.conditional_questions_tooltip')}
                        </span>
                    </span>
                </div>
                <div className='conditional-question-rows'>
                    {conditionOptions.map(conditionOption => {

                        // Get the conditional question linked to this answer option
                        const childField = getChildFieldForConditionOption(field, childFields, conditionOption);

                        return (
                            <div key={String(conditionOption.value)} className='conditional-question-row'>
                                <span className='option-name'>
                                    {conditionOption.name}
                                </span>
                                {childField ? (
                                    <span
                                        className='conditional-question-action remove'
                                        onClick={() => handleRemoveConditionalQuestion(childField)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                        {t('custom_field.general.remove_conditional_question_label')}
                                    </span>
                                ) : (
                                    <span
                                        className='conditional-question-action add'
                                        onClick={() => handleAddConditionalQuestion(conditionOption)}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        {t('custom_field.general.add_conditional_question_label')}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ConditionalFieldsEditor;
